import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import api from '../services/api';
import searchIcon from '../assets/images/search_icon.svg';
import arrowIcon from '../assets/images/Arrow.svg';
import addIcon from '../assets/images/add-icon.svg';
import editIcon from '../assets/images/edit_icon.svg';
import upwardArrowIcon from '../assets/images/upward_arrow.svg';

import "../styles/dataTable.css";

const ManageAdminUser = () => {
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);

  // Table
  const [search, setSearch] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");

  // --------------------------------------------------
  // FILTER
  // --------------------------------------------------

  const filteredData = users.filter((item) =>
    Object.values(item)
      .join(" ")
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  // --------------------------------------------------
  // SORT
  // --------------------------------------------------

  if (sortField) {
    filteredData.sort((a, b) => {
      const valA = a[sortField];
      const valB = b[sortField];

      if (typeof valA === "number") {
        return sortOrder === "asc"
          ? valA - valB
          : valB - valA;
      }

      return sortOrder === "asc"
        ? String(valA).localeCompare(String(valB))
        : String(valB).localeCompare(String(valA));
    });
  }

  // --------------------------------------------------
  // PAGINATION
  // --------------------------------------------------

  const totalPages = Math.ceil(
    filteredData.length / entriesPerPage
  );

  const paginatedData = filteredData.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage
  );

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [search, entriesPerPage, totalPages, currentPage]);

  // --------------------------------------------------
  // FETCH USERS
  // --------------------------------------------------

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get("/adminusers/get-all");

      const usersWithStatus = (response.data.data || []).map((u) => ({
        ...u,
        leadstatus: u.leadstatus
          ? Number(u.leadstatus)
          : 1
      }));

      setUsers(usersWithStatus);

    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  // --------------------------------------------------
  // SORT HANDLER
  // --------------------------------------------------

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) =>
        prev === "asc" ? "desc" : "asc"
      );
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  // --------------------------------------------------
  // USER STATUS
  // --------------------------------------------------

  const toggleUserStatus = async (id, status) => {
    try {

      let res;

      if (status) {
        res = await api.get(`/adminusers/inactive/${id}`);
      } else {
        res = await api.get(`/adminusers/active/${id}`);
      }

      if (res.data.status === true) {
        fetchUsers();
      } else {
        alert("Status not changed");
      }

    } catch (error) {
      console.error("Error changing user status:", error);
      alert("Error changing user status");
    }
  };

  // --------------------------------------------------
  // CREATE USER
  // --------------------------------------------------

  const handleCreateUserClick = () => {
    navigate("/users/add");
  };

  // --------------------------------------------------
  // UPDATE USER
  // --------------------------------------------------

  const handleUserUpdate = (id) => {
    navigate(`/users/edit/${id}`);
  };

  return (
    <div>

      <div style={{ padding: "20px" }}>

        {/* ================= HEADER ================= */}

        <div className="table-header">

          <h2 style={{ fontWeight: 700 }}>
            User List
          </h2>

          {/* Search */}

          <div className="search-container">

            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search"
            />

            <img
              src={searchIcon}
              alt="search-icon"
              className="search-icon"
            />

          </div>

          {/* Entries */}

          <div className="entries-container">

            <label htmlFor="noOfEntries">
              Show:
            </label>

            <select
              value={entriesPerPage}
              id="noOfEntries"
              onChange={(e) =>
                setEntriesPerPage(
                  Number(e.target.value)
                )
              }
            >
              <option value={10}>
                10 Entries per page
              </option>

              <option value={50}>
                50 Entries per page
              </option>

              <option value={100}>
                100 Entries per page
              </option>

            </select>

          </div>

          {/* Add User */}

          <div className="action-btn">

            <button onClick={handleCreateUserClick}>

              <img
                src={addIcon}
                alt="add-icon"
              />

              Add User

            </button>

          </div>

        </div>


        {/* ================= TABLE ================= */}

        <table
          width="100%"
          cellPadding="20"
          className="table-no-outer-border"
        >

          <thead>

            <tr>

              <th
                onClick={() =>
                  handleSort("emplyid")
                }
              >

                <div className="name-container">

                  Employee Id

                  <div className="sort-arrow-container">

                    <img
                      src={upwardArrowIcon}
                      alt="up-arrow"
                      style={{
                        opacity:
                          sortField === "emplyid" &&
                          sortOrder === "asc"
                            ? 1
                            : 0.5
                      }}
                    />

                    <img
                      src={upwardArrowIcon}
                      alt="down-arrow"
                      style={{
                        transform:
                          "rotateX(180deg)",
                        marginTop: "5px",
                        opacity:
                          sortField === "emplyid" &&
                          sortOrder === "desc"
                            ? 1
                            : 0.5
                      }}
                    />

                  </div>

                </div>

              </th>

              <th>Name</th>

              <th>Email</th>

              <th>Status</th>

              <th>Edit</th>

            </tr>

          </thead>


          <tbody>

            {paginatedData.length > 0 ? (

              paginatedData.map((row) => (

                <tr key={row.id}>

                  <td>
                    {row.emplyid}
                  </td>

                  <td>
                    {row.name}
                  </td>

                  <td>
                    {row.email}
                  </td>


                  {/* STATUS */}

                  <td className="text-center align-middle">

                    <div className="custom-toggle-switch">

                      <input
                        type="checkbox"
                        className="custom-toggle-input"
                        checked={row.status === "1"}
                        onChange={() =>
                          toggleUserStatus(
                            row.id,
                            row.status === "1"
                          )
                        }
                        id={`toggle-${row.id}`}
                      />

                      <label
                        className="custom-toggle-label"
                        htmlFor={`toggle-${row.id}`}
                      >

                        <span className="custom-toggle-text">

                          {row.status === "1"
                            ? "Active"
                            : "Inactive"}

                        </span>

                      </label>

                    </div>

                  </td>


                  {/* EDIT */}

                  <td className="text-center align-middle">

                    <button
                      className="edit-button"
                      onClick={() =>
                        handleUserUpdate(row.id)
                      }
                    >

                      <img
                        src={editIcon}
                        alt="editIcon"
                      />

                      Edit

                    </button>

                  </td>

                </tr>

              ))

            ) : (

              <tr>

                <td
                  colSpan={5}
                  style={{
                    textAlign: "center"
                  }}
                >
                  No data found
                </td>

              </tr>

            )}

          </tbody>

        </table>


        {/* ================= PAGINATION ================= */}

        <div
          style={{
            paddingBottom: "30px",
            background: "#fff"
          }}
        >

          <div
            style={{
              padding: "30px",
              textAlign: "center",
              borderTop: "2px solid #F2F2F2",
              borderBottom: "2px solid #F2F2F2"
            }}
          >

            {/* Previous */}

            <button
              onClick={() =>
                setCurrentPage((prev) =>
                  Math.max(prev - 1, 1)
                )
              }
              disabled={currentPage === 1}
              style={{
                margin: "0 5px",
                border: "none",
                background: "#fff"
              }}
            >

              <img
                src={arrowIcon}
                alt="prev"
                style={{
                  opacity:
                    currentPage === 1
                      ? 0.5
                      : 1
                }}
              />

            </button>


            {/* Pages */}

            {Array.from(
              { length: totalPages },
              (_, i) => i + 1
            ).map((page) => (

              <button
                key={page}
                onClick={() =>
                  setCurrentPage(page)
                }
                style={{
                  margin: "0 5px",
                  padding: "5px 10px",
                  background:
                    currentPage === page
                      ? "#D9D9D9"
                      : "#fff",
                  border: "none",
                  cursor: "pointer"
                }}
              >

                {page}

              </button>

            ))}


            {/* Next */}

            <button
              onClick={() =>
                setCurrentPage((prev) =>
                  Math.min(
                    prev + 1,
                    totalPages
                  )
                )
              }
              disabled={
                currentPage === totalPages ||
                totalPages === 0
              }
              style={{
                margin: "0 5px",
                border: "none",
                background: "#fff"
              }}
            >

              <img
                src={arrowIcon}
                alt="next"
                style={{
                  opacity:
                    currentPage === totalPages ||
                    totalPages === 0
                      ? 0.5
                      : 1,
                  transform:
                    "rotateY(180deg)"
                }}
              />

            </button>

          </div>

        </div>

      </div>

    </div>
  );
};

export default ManageAdminUser;